import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, Phone, ImageOff } from "lucide-react";

import SEO from "@/components/SEO";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import CTASection from "@/components/CTASection";
import CategoryChips from "@/components/portfolio/CategoryChips";
import ProjectCard from "@/components/portfolio/ProjectCard";
import LightboxModal from "@/components/portfolio/LightboxModal";
import { publicClient } from "@/lib/apiClient";
import { PORTFOLIO_CATEGORIES } from "@/data/portfolioCategories";
import { SITE } from "@/lib/site";

const BREADCRUMBS = [{ label: "Home", to: "/" }, { label: "Portfolio" }];

export default function Portfolio() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  // Initial fetch
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    publicClient
      .get("/projects")
      .then((res) => {
        if (cancelled) return;
        setProjects(Array.isArray(res.data) ? res.data : []);
        setError("");
      })
      .catch(() => {
        if (cancelled) return;
        setError("We couldn't load the portfolio right now. Please try again in a moment.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const counts = useMemo(() => {
    const result = { all: projects.length };
    for (const cat of PORTFOLIO_CATEGORIES) {
      result[cat.slug] = projects.filter((p) => p.category === cat.slug).length;
    }
    return result;
  }, [projects]);

  const filteredProjects = useMemo(() => {
    if (activeCategory === "all") return projects;
    return projects.filter((p) => p.category === activeCategory);
  }, [projects, activeCategory]);

  const handleOpen = useCallback((project) => setLightbox(project), []);
  const handleClose = useCallback(() => setLightbox(null), []);

  return (
    <>
      <SEO
        title="Project Portfolio Austin TX | Black Ridge Construction"
        description="Browse roofing, remodeling, outdoor living, exterior, and repair projects completed by Black Ridge Construction across Austin and Central Texas."
      />

      {/* ───────────────────────── Hero ───────────────────────── */}
      <section className="bg-br-black text-white">
        <div className="container-br py-12 lg:py-20">
          <BreadcrumbNav dark items={BREADCRUMBS} />
          <div className="max-w-3xl mt-5">
            <div className="br-eyebrow text-br-bronze-2">Project Portfolio</div>
            <h1 className="br-headline text-4xl sm:text-5xl mt-3">
              Real Work, Documented &amp; Done Right
            </h1>
            <p className="mt-4 text-gray-300 leading-relaxed">
              A growing collection of roofing, remodeling, outdoor living, exterior, and repair projects completed across Austin and Central Texas. Each project on this page reflects real work by the Black Ridge Construction team.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/contact" className="br-btn-primary" data-testid="portfolio-hero-quote">
                Get a Quote for Your Project <ArrowRight size={14} />
              </Link>
              <a href={`tel:${SITE.phoneE164}`} className="br-btn-secondary" data-testid="portfolio-hero-call">
                <Phone size={14} /> Call {SITE.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────── Gallery ───────────────────────── */}
      <section className="section-pad bg-br-paper">
        <div className="container-br">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <div className="br-eyebrow">Browse the Gallery</div>
              <h2 className="br-headline text-2xl sm:text-3xl mt-2">Filter by Project Type</h2>
            </div>
            <div className="text-xs text-gray-500">
              {projects.length} project{projects.length === 1 ? "" : "s"} published
            </div>
          </div>

          <CategoryChips
            active={activeCategory}
            counts={counts}
            onChange={setActiveCategory}
          />

          <div className="mt-8" data-testid="portfolio-grid">
            {loading && (
              <div className="flex items-center justify-center py-20 text-gray-500" data-testid="portfolio-loading">
                <Loader2 className="animate-spin mr-2" size={18} /> Loading projects…
              </div>
            )}
            {!loading && error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 p-4 rounded" data-testid="portfolio-error">
                {error}
              </div>
            )}
            {!loading && !error && filteredProjects.length === 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-10 text-center" data-testid="portfolio-empty">
                <div className="w-12 h-12 mx-auto rounded-full bg-br-paper-2 flex items-center justify-center text-br-bronze mb-3">
                  <ImageOff size={20} />
                </div>
                <div className="font-display tracking-widest uppercase text-lg text-br-black">No projects published yet</div>
                <p className="mt-2 text-sm text-gray-600 max-w-md mx-auto">
                  {activeCategory === "all"
                    ? "We're building out the portfolio now. Check back soon — or get in touch and we'll share recent project photos directly."
                    : "No projects in this category yet. Try another filter or contact us for project examples."}
                </p>
                <Link to="/contact" className="br-btn-dark mt-5" data-testid="portfolio-empty-contact">
                  Contact Black Ridge <ArrowRight size={14} />
                </Link>
              </div>
            )}
            {!loading && !error && filteredProjects.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredProjects.map((p) => (
                  <ProjectCard key={p.id} project={p} onOpen={handleOpen} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to Start Your Project?"
        subtitle="Talk to Black Ridge Construction about your roofing, remodeling, or repair project. We'll review the scope, walk the property, and give you a clear, honest quote."
        primaryLabel="Get a Quote"
      />

      <LightboxModal project={lightbox} onClose={handleClose} />
    </>
  );
}
